import { supabase, isSupabaseEnabled } from './supabase';

const AUTH_STORAGE_KEY = 'shadowland-auth';
const EXPIRY_BUFFER_MS = 30_000;

let cachedSession = null;
let initialSessionPromise = null;
let listenerStarted = false;

function authStorages() {
  if (typeof window === 'undefined') return [];
  return [window.localStorage, window.sessionStorage].filter(Boolean);
}

function sessionFromStorageValue(value) {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    if (parsed?.access_token) return parsed;
    if (parsed?.currentSession?.access_token) return parsed.currentSession;
    if (parsed?.session?.access_token) return parsed.session;
  } catch {
    return null;
  }
  return null;
}

function isExpiredOrExpiring(session, bufferMs = EXPIRY_BUFFER_MS) {
  const expiresAt = Number(session?.expires_at || 0);
  if (!expiresAt) return false;
  return expiresAt * 1000 <= Date.now() + bufferMs;
}

function readStoredSession() {
  for (const storage of authStorages()) {
    try {
      const session = sessionFromStorageValue(storage.getItem(AUTH_STORAGE_KEY));
      if (session?.access_token && !isExpiredOrExpiring(session)) return session;
    } catch {
      // Storage access can fail in private/incognito contexts.
    }
  }
  return null;
}

function setCachedSession(session) {
  cachedSession = session?.access_token ? session : null;
  return cachedSession;
}

function timeout(ms) {
  return new Promise((resolve) => setTimeout(() => resolve(null), ms));
}

async function withTimeout(promise, ms) {
  return Promise.race([promise, timeout(ms)]);
}

function ensureAuthListener() {
  if (listenerStarted || !isSupabaseEnabled || !supabase) return;
  listenerStarted = true;
  supabase.auth.onAuthStateChange((_event, session) => {
    setCachedSession(session);
  });
}

async function loadInitialSession(timeoutMs = 3000) {
  if (!isSupabaseEnabled || !supabase) return null;
  ensureAuthListener();
  if (initialSessionPromise) return initialSessionPromise;

  initialSessionPromise = (async () => {
    const stored = readStoredSession();
    if (stored) setCachedSession(stored);

    const result = await withTimeout(supabase.auth.getSession(), timeoutMs).catch(() => null);
    const session = result?.data?.session || null;
    if (session?.access_token) return setCachedSession(session);
    return cachedSession;
  })();

  return initialSessionPromise;
}

export async function waitForAuthReady(timeoutMs = 3000) {
  return loadInitialSession(timeoutMs);
}

export async function getAccessToken({ forceRefresh = false, timeoutMs = 3000 } = {}) {
  if (!isSupabaseEnabled || !supabase) return null;
  ensureAuthListener();

  await loadInitialSession(timeoutMs);

  let session = cachedSession;
  if (forceRefresh || !session?.access_token || isExpiredOrExpiring(session)) {
    const refreshed = await withTimeout(supabase.auth.refreshSession(), timeoutMs).catch(() => null);
    if (refreshed?.data?.session?.access_token && !refreshed?.error) {
      session = setCachedSession(refreshed.data.session);
    } else {
      const current = await withTimeout(supabase.auth.getSession(), timeoutMs).catch(() => null);
      if (current?.data?.session?.access_token) {
        session = setCachedSession(current.data.session);
      }
    }
  }

  if (!session?.access_token) session = readStoredSession();
  if (!session?.access_token || isExpiredOrExpiring(session, 0)) return null;
  return session.access_token;
}

export function clearAuthSessionCache() {
  cachedSession = null;
  initialSessionPromise = null;
}
